import * as React from 'react';
import uniqBy from 'lodash/uniqBy';
import {useNavigate} from 'react-router';
import omit from 'lodash/omit';

import {SearchSortOption} from 'common/report/util/search/types';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useDashboardSelectorController} from 'common/contexts/react/dashboard-selector-controller-context';
import {useOpenInNewTab} from 'common/contexts/react/open-in-new-tab-context';
import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {useLocalStorageManager} from 'common/contexts/react/storage-manager-context';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {LocalStorageKey, Permission, ProjectFeatureFlag} from 'common/report/constants';
import {
  ContentItemInfo,
  ContentSidenavContextState,
  ContentSidenavContextValue,
  ContentSidenavSection,
  ContentSidenavSectionItem,
  DashboardItemInfo,
  DeleteSidenavItemFn,
  GetSidenavPropsFn,
  InsertContentSidenavItemFn,
  LoadSidenavSectionsFn,
  MoveSidenavItemFn,
  UpdateContentSidenavItemFn,
  UpdateSidenavSectionsFn,
} from 'common/widgets/content-sidenav/types';
import {
  DragDropEventDetail,
  SidenavCta,
  SidenavHeader,
  SidenavItemPosition,
  SidenavProps,
  SidenavSectionItem,
} from 'common/components/sidenav/types';
import {
  contentItemInfoToDashboardItemInfo,
  contentToSidenavSectionItem,
  copySidenavLink,
  dashboardItemInfoToContentItemInfo,
  findFirstSidenavItemWithValue,
  getSectionsAfterMovingSidenavItem,
  getSidenavItemParentValue,
  getSidenavSections,
  getUserId,
  isCrossProjectBoardMovesEnabled,
  isOrgOnEnterprisePlan,
  isValueInDashboardItem,
  moveBoardViaDashboardClient,
  MOVED_BOARD_STORAGE_KEY,
  moveSidenavItemAtTopLevel,
  reportErrorOnMovingBoard,
  SidenavHeaderAction,
  updateSidenavOrdering,
} from 'common/widgets/content-sidenav/util';
import {getCardIdFromContent, isNestedDashboardsEnabled} from 'common/report/util/dashboards/utils';
import {
  SidenavOverflowAction,
  SidenavSectionIndex,
  SidenavSectionTrackingName,
} from 'common/widgets/content-sidenav/constants';
import {getSidenavItemWithoutValue, sidenavItemSplice} from 'common/components/sidenav/util';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {dashboardsClient} from 'common/report/queries/app-api/dashboards-client';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {reporter} from 'common/report/error-reporting';
import {StagedDashboard} from 'common/report/dashboards/staged-dashboard';
import {ContentType, Dashboard} from 'common/report/dashboards/types';
import {confirm} from 'common/components/confirm';
import {
  buildCreateEntityModalAttrs,
  getEntityType,
  getEntityTypeName,
  isAllowedSuperadminOverride,
  maybeAddGlobalAccessTypeParam,
  normalizeContentEntity,
} from 'common/report/util/saved-entities';
import {THEME as ConfirmTheme} from 'common/components/confirm/types';
import {ApiResponseError} from 'common/util/shared-client';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {sharingWidget} from 'common/widgets/sharing';
import {SharableEntityType} from 'common/report/util/saved-entities/types';
import {GlobalAccessType} from 'common/widgets/create-entity-modal/types';
import {useTranslation} from 'common/i18n/hooks';
import {WEB_DASH_PREFIX} from 'common/util/timing';
import {useUserDetails} from 'common/contexts/react/user-details-context';
import {
  DashboardCRUDSource,
  getCommonDashboardTrackingProps,
  ProductionTrackingEvents,
  TrackingEvent as DashboardTrackingEvent,
} from 'common/report/dashboards/tracking';
import {getReportTracker} from 'common/report/report-manager/tracking';
import {ReportTracker} from 'common/report/report-tracker';
import {useReportInfo} from 'common/contexts/react/report-info-context';
import {serializeDashboardUrlHashParams} from 'common/report/dashboards/url';
import {
  getCommonContentTrackingProps,
  TrackingEvent as ContentTrackingEvent,
  TrackingSource as ContentTrackingSource,
} from 'common/content/tracking';
import {DashboardsModelClient} from 'common/report/controllers/types';
import {useDeleteContent} from 'common/content/hooks/useDeleteContent';
import {usePinContent} from 'common/content/hooks/usePinContent';
import {useFavoriteContent} from 'common/content/hooks/useFavoriteContent';
import {useRenameContent} from 'common/content/hooks/useRenameContent';
import {useDuplicateContent} from 'common/content/hooks/useDuplicateContent';
import {NEW_EXPERIMENT_ID} from 'apps/experiments/src/components/utils';
import {hasAccessToExperimentsApp} from 'common/util/experiments-feature-gate';
import {isCanvasEnabled} from 'common/report/util/feature-sampling';
import {TagTheme} from 'iron/routes/global-sidenav/types';

import {promptManageContentModal, promptManageDashboardModal} from '../create-entity-modal';

const SIDENAV_HEADER_ACTION = {
  LastModified: SearchSortOption.LastModified,
  Browse: `browse`,
  Home: `home`,
  New: `new`,
  NewCanvas: `new-canvas`,
  NewExperiment: `new-experiment`,
  NewFeatureFlag: `new-feature-flag`,
};

/**
 * ContentSidenavContext should only be used in reports that have a content sidenav. However, there can be cases
 * in these reports where the sidenav shouldn't render, e.g. embed/download mode.
 */
export const ContentSidenavContext = React.createContext<ContentSidenavContextValue | undefined>(undefined);

export const ContentSidenavProvider: React.FC<React.PropsWithChildren> = (props) => {
  const [sidenavState, setSidenavState] = React.useState<ContentSidenavContextState>({});

  const [isLoading, setIsLoading] = React.useState(true);
  const [sidenavSections, setSidenavSections] = React.useState<ContentSidenavSection[]>([]);

  const userDetailsStore = useUserDetails();
  const currentProject = useCurrentProject();
  const navigate = useNavigate();
  const selectorController = useDashboardSelectorController();
  const openInNewTab = useOpenInNewTab();
  const i18n = useTranslation();
  const {t} = i18n;

  const projectId = getProjectId();
  const workspaceId = getWorkspaceId();
  const userId = getUserId() ?? 0;

  const trackerRef = React.useRef<ReportTracker>();
  const reportInfo = useReportInfo();

  const localStorageManager = useLocalStorageManager();

  const isDashboardItemInfo = (content: ContentItemInfo) => {
    return content.content_type === SharableEntityType.Dashboard;
  };

  const updateSidenavState = (
    updater:
      | Partial<ContentSidenavContextState>
      | ((prevState: ContentSidenavContextState) => Partial<ContentSidenavContextState>),
  ) => {
    setSidenavState((prevState) => {
      return {
        ...prevState,
        ...(typeof updater === `function` ? updater(prevState) : updater),
      };
    });
  };

  const currentProjectValue = currentProject.get().value;

  const isDashboardsOnly =
    !isCanvasEnabled(currentProjectValue || null) &&
    !hasAccessToExperimentsApp(currentProjectValue) &&
    !currentProjectValue?.hasFeatureFlag(ProjectFeatureFlag.FeatureFlagsApp);

  const movedBoardInfoStorage = React.useRef(
    new UserLocalStorageHelper({
      namespace: LocalStorageKey.CrossProject,
      context: localStorageManager,
    }),
  );

  const seenPinnedContentIdsStorageRef = React.useRef(
    new UserLocalStorageHelper<string[]>({
      namespace: LocalStorageKey.SeenPinnedContentIds,
      context: localStorageManager,
    }),
  );
  const seenPinnedContentIdsStorage = seenPinnedContentIdsStorageRef.current;

  const deleteContentRequest = useDeleteContent({
    projectId,
    workspaceId,
    source: ContentTrackingSource.Sidenav,
  });
  const pinContentRequest = usePinContent({
    projectId,
    workspaceId,
    userId,
    source: ContentTrackingSource.Sidenav,
  });
  const favoriteContentRequest = useFavoriteContent({
    projectId,
    workspaceId,
    source: ContentTrackingSource.Sidenav,
  });
  const renameContentRequest = useRenameContent({
    projectId,
    source: ContentTrackingSource.Sidenav,
  });
  const duplicateContentRequest = useDuplicateContent({
    projectId,
    workspaceId,
    redirectAfterSuccess: true,
    source: ContentTrackingSource.Sidenav,
  });

  React.useEffect(() => {
    if (trackerRef.current) {
      trackerRef.current = getReportTracker({
        appName: reportInfo.getReportName(),
        productionEvents: ProductionTrackingEvents,
        productionPrefix: WEB_DASH_PREFIX,
      });
    }
  }, [reportInfo.getReportName()]);

  React.useEffect(() => {
    userDetailsStore.fetch();
  }, []);

  const trackEvent = (...args: Parameters<NonNullable<ReportTracker>[`track`]>) => {
    if (trackerRef.current) {
      trackerRef.current.track(...args);
    }
  };

  const handleSidenavSelectItem = async (ev: CustomEvent<SidenavSectionItem>): Promise<void> => {
    const {detail} = ev;
    if (!detail.content) {
      return;
    }

    if (detail.content.id !== sidenavState.selectedNavItem && sidenavState.confirmContentNavigate) {
      const canNavigate = await sidenavState.confirmContentNavigate();
      if (!canNavigate) {
        return;
      }
    }

    window.scrollTo(0, 0);

    const isDashboard = isDashboardItemInfo(detail.content);
    if (isDashboard) {
      trackDashboardEvent(
        contentItemInfoToDashboardItemInfo(detail.content),
        DashboardTrackingEvent.SelectDashboard,
        detail.trackingName,
      );
    } else {
      trackContentEvent(detail.content, ContentTrackingEvent.SelectContent, detail.trackingName);
    }
  };

  const getSidenavHeader = (): SidenavHeader => {
    const isHomeDisabled = currentProjectValue?.hasFeatureFlag(ProjectFeatureFlag.DisableHome);
    const actions: SidenavCta[] = [
      {
        label: t(`sidenav.discover`),
        value: SIDENAV_HEADER_ACTION.Browse,
        icon: `discover`,
        href:
          getReportUrl(`boards`, {
            projectId: getProjectId(),
            workspaceId: getWorkspaceId(),
          }) + `#discover`,
      },
      {
        label: t(`sidenav.new-board`),
        value: SIDENAV_HEADER_ACTION.New,
        icon: `plus`,
      },
    ];

    if (!isHomeDisabled) {
      actions.unshift({
        label: t(`mixology.app.home`),
        value: SIDENAV_HEADER_ACTION.Home,
        icon: `home`,
        href: getReportUrl(`home`, {
          projectId: getProjectId(),
          workspaceId: getWorkspaceId(),
        }),
      });
    }

    let showAddExperiment = currentProjectValue?.hasFeatureFlag(ProjectFeatureFlag.ExperimentsApp);
    if (showAddExperiment) {
      const permissions = userDetailsStore.value.projects?.[currentProjectValue?.id ?? 0]?.permissions || [];
      showAddExperiment = permissions.includes(Permission.WriteExperiments) || userDetailsStore.value.isStaff;
    }
    if (showAddExperiment) {
      actions.push({
        label: t(`sidenav.new-experiment`),
        value: SIDENAV_HEADER_ACTION.NewExperiment,
        icon: `plus`,
        href: getReportUrl(`experiments/${NEW_EXPERIMENT_ID}`, {
          projectId: getProjectId(),
          workspaceId: getWorkspaceId(),
        }),
        tag: i18n.t(`common.new`),
        tagTheme: TagTheme.Neutral,
      });
    }

    let showAddFeatureFlag = currentProjectValue?.hasFeatureFlag(ProjectFeatureFlag.FeatureFlagsApp);
    if (showAddFeatureFlag) {
      const permissions = userDetailsStore.value.projects?.[currentProjectValue?.id ?? 0]?.permissions || [];
      showAddFeatureFlag = permissions.includes(Permission.WriteFeatureFlags) || userDetailsStore.value.isStaff;
    }
    if (showAddFeatureFlag) {
      actions.push({
        label: t(`sidenav.new-feature-flag`),
        value: SIDENAV_HEADER_ACTION.NewFeatureFlag,
        icon: `plus`,
        href: getReportUrl(`feature-flags/new`, {
          projectId: getProjectId(),
          workspaceId: getWorkspaceId(),
        }),
        tag: t(`common.beta`),
      });
    }

    let showAddCanvas = isCanvasEnabled(currentProjectValue || null);
    if (showAddCanvas) {
      const permissions = userDetailsStore.value.projects?.[currentProjectValue?.id ?? 0]?.permissions || [];
      showAddCanvas = permissions.includes(Permission.WriteMetricTrees) || userDetailsStore.value.isStaff;
    }
    if (showAddCanvas) {
      actions.push({
        label: t(`sidenav.new-canvas`),
        value: SIDENAV_HEADER_ACTION.NewCanvas,
        icon: `plus`,
        href: getReportUrl(`metric-tree`, {
          projectId: getProjectId(),
          workspaceId: getWorkspaceId(),
        }),
        tag: t(`common.alpha`),
      });
    }

    return {actions};
  };

  React.useEffect(() => {
    getSidenavSections({
      userId,
      projectId: parseInt(getProjectId()),
      seenPinnedContentIdsStorage,
      hideDashboardList: !isNestedDashboardsEnabled(currentProjectValue!),
      dashboardSelectorController: selectorController,
      isEnterpriseFeatureGateTier: isOrgOnEnterprisePlan(currentProjectValue!),
      isNestedDashboardsEnabled: isNestedDashboardsEnabled(currentProjectValue!),
      isCrossProjectBoardMovesEnabled: isCrossProjectBoardMovesEnabled(currentProjectValue!),
      isDashboardsOnly,
    }).then((sections) => {
      setSidenavSections(sections);
      setIsLoading(false);
    });
  }, [isDashboardsOnly]);

  const myDashboardTrackingProps = (trackedDashboardId: number) => {
    return {
      'is my dashboard':
        !!userDetailsStore.value.myDashboardId && trackedDashboardId === userDetailsStore.value.myDashboardId,
    };
  };

  const updateContentSidenavItemAndChildren = (
    content: DashboardItemInfo | ContentItemInfo,
    item: ContentSidenavSectionItem,
    sectionIndex: number,
    isEnterpriseFeatureGateTier: boolean,
    favoriteIds?: Array<string | number>,
    pinnedIds?: Array<string | number>,
  ): ContentSidenavSectionItem => {
    if (content.id.toString() === item.value) {
      return contentToSidenavSectionItem({
        content,
        sectionIndex,
        trackingName: item.trackingName,
        isNew: false,
        selectorController,
        isEnterpriseFeatureGateTier,
        isNestedDashboardsEnabled: isNestedDashboardsEnabled(currentProjectValue!),
        isCrossProjectBoardMovesEnabled: isCrossProjectBoardMovesEnabled(currentProjectValue!),
        hideDashboardList: !isNestedDashboardsEnabled(currentProjectValue!),
        favoriteIds,
        pinnedIds,
        isDashboardsOnly,
      });
    }

    if (item.nestedItems) {
      const newChildren = item.nestedItems.map((childItem) => {
        return updateContentSidenavItemAndChildren(
          content,
          childItem as ContentSidenavSectionItem,
          sectionIndex,
          isEnterpriseFeatureGateTier,
          favoriteIds,
          pinnedIds,
        );
      });

      return {...item, nestedItems: newChildren};
    }
    return item;
  };

  const updateContentSidenavItem: UpdateContentSidenavItemFn = (params: {
    content: ContentItemInfo;
    sidenavSections?: ContentSidenavSection[];
  }) => {
    const currSidenavSections = params.sidenavSections || sidenavSections;

    const favoriteIds =
      currSidenavSections[SidenavSectionIndex.Favorites]?.items?.map(
        (item: ContentSidenavSectionItem) => item?.content?.id ?? ``,
      ) ?? [];
    const pinnedIds =
      currSidenavSections[SidenavSectionIndex.Pinned]?.items?.map(
        (item: ContentSidenavSectionItem) => item?.content?.id ?? ``,
      ) ?? [];

    let updatedSidenavSections = [...currSidenavSections];

    for (const [sectionIndex, section] of currSidenavSections.entries()) {
      for (const [itemIndex, item] of section.items.entries()) {
        const newItem = updateContentSidenavItemAndChildren(
          params.content,
          item,
          sectionIndex,
          isOrgOnEnterprisePlan(currentProjectValue!),
          favoriteIds,
          pinnedIds,
        );
        updatedSidenavSections = sidenavItemSplice(updatedSidenavSections, sectionIndex, itemIndex, 1, newItem);
      }
    }

    setSidenavSections(updatedSidenavSections as ContentSidenavSection[]);
  };

  const insertContentSidenavItem: InsertContentSidenavItemFn = (content, sectionIndex, trackingName) => {
    const sidenavItem = contentToSidenavSectionItem({
      content,
      sectionIndex,
      trackingName,
      isNew: false,
      selectorController,
      isEnterpriseFeatureGateTier: isOrgOnEnterprisePlan(currentProjectValue!),
      isNestedDashboardsEnabled: isNestedDashboardsEnabled(currentProjectValue!),
      isCrossProjectBoardMovesEnabled: isCrossProjectBoardMovesEnabled(currentProjectValue!),
      hideDashboardList: !isNestedDashboardsEnabled(currentProjectValue!),
      isDashboardsOnly,
    });
    const updatedSidenavSections = sidenavItemSplice(
      sidenavSections,
      sectionIndex,
      sidenavSections[sectionIndex]?.items?.length ?? 0,
      0,
      sidenavItem,
    );
    if (!sidenavSections[sectionIndex]?.items?.find((item) => item.value === sidenavItem.value)) {
      setSidenavSections(updatedSidenavSections);
      updateContentSidenavItem({content, sidenavSections: updatedSidenavSections});
    }
  };

  const addOrRemoveContentFromSidenavSectionTopLevel = (
    content: ContentItemInfo,
    shouldBeInSection: boolean,
    sectionIndex: number,
    sectionTrackingName: SidenavSectionTrackingName,
  ) => {
    const isInSectionAtTopLevel = sidenavSections[sectionIndex]?.items?.some(
      (item: ContentSidenavSectionItem) => item.value === content.id,
    );
    if (!shouldBeInSection && isInSectionAtTopLevel) {
      const sections = sidenavItemSplice(
        sidenavSections,
        sectionIndex,
        sidenavSections[sectionIndex]?.items?.findIndex((item) => {
          return item.value === content.id.toString();
        }) ?? -1,
        1,
      );
      setSidenavSections(sections);
      updateContentSidenavItem({content, sidenavSections: sections});
    } else if (shouldBeInSection && !isInSectionAtTopLevel) {
      insertContentSidenavItem(content, sectionIndex, sectionTrackingName);
    }
  };

  const updateSidenavSections: UpdateSidenavSectionsFn = ({content}: {content: ContentItemInfo}) => {
    addOrRemoveContentFromSidenavSectionTopLevel(
      content,
      Boolean(content.is_favorited),
      SidenavSectionIndex.Favorites,
      SidenavSectionTrackingName.Favorites,
    );
    addOrRemoveContentFromSidenavSectionTopLevel(
      content,
      Boolean(content.pinned_date),
      SidenavSectionIndex.Pinned,
      SidenavSectionTrackingName.Pinned,
    );
    addOrRemoveContentFromSidenavSectionTopLevel(
      content,
      content.creator_id === userCookieUtils.getUserIdentityFromCookie()?.id && !content.parent_dashboard_id,
      SidenavSectionIndex.Yours,
      SidenavSectionTrackingName.Yours,
    );
  };

  function trackDashboardEvent(dashboard: DashboardItemInfo, event?: DashboardTrackingEvent, trackingName?: string) {
    if (event) {
      trackEvent(event, {
        ...getCommonDashboardTrackingProps({
          dashboard,
          sourceTrackingProps: {crudSource: DashboardCRUDSource.Sidenav},
        }),
        ...(trackingName ? {'Sidenav Section': trackingName} : {}),
      });
    }
  }

  function trackContentEvent(content: ContentItemInfo, event?: ContentTrackingEvent, trackingName?: string) {
    if (event) {
      trackEvent(event, {
        ...getCommonContentTrackingProps({
          content,
          source: ContentTrackingSource.Sidenav,
        }),
        ...(trackingName ? {'Sidenav Section': trackingName} : {}),
      });
    }
  }

  async function performDashboardAction<T>({
    dashboard,
    action,
    onSuccess,
    onComplete,
    successMessage,
    errorMessage,
    initialTrackingEvent,
    successTrackingEvent,
    errorTrackingEvent,
  }: {
    dashboard: DashboardItemInfo;
    action: () => Promise<T>;
    onSuccess?: () => void;
    onComplete?: () => void;
    successMessage?: string;
    errorMessage?: string;
    initialTrackingEvent?: DashboardTrackingEvent;
    successTrackingEvent?: DashboardTrackingEvent;
    errorTrackingEvent?: DashboardTrackingEvent;
  }): Promise<T | undefined> {
    trackDashboardEvent(dashboard, initialTrackingEvent);
    try {
      const result = await action();
      trackDashboardEvent(dashboard, successTrackingEvent);
      if (onSuccess) {
        onSuccess();
      }
      if (successMessage) {
        addToast({
          type: ToastType.SUCCESS,
          message: successMessage,
          timeout: 20000,
        });
      }
      return result;
    } catch (err) {
      reporter.error(err as Error, errorMessage);
      trackDashboardEvent(dashboard, errorTrackingEvent);
      if (errorMessage) {
        addToast({
          type: ToastType.ERROR,
          message: errorMessage,
          timeout: 20000,
        });
      }
      return undefined;
    } finally {
      if (onComplete) {
        onComplete();
      }
    }
  }

  const favoriteContent = (content: ContentItemInfo) => {
    const currSidenavSections = sidenavSections;

    if (isDashboardItemInfo(content)) {
      const favoriteIds = currSidenavSections[SidenavSectionIndex.Favorites].items.map(
        (item) => item.content?.id ?? ``,
      );
      const isFavorited = !favoriteIds.includes(content.id);
      const dashboard = contentItemInfoToDashboardItemInfo(content);
      performDashboardAction({
        dashboard,
        action: () => {
          return dashboardsClient.favoriteDashboard({
            projectId,
            workspaceId: parseInt(workspaceId),
            dashboardID: dashboard.id,
            isFavorited,
          });
        },
        onSuccess: () => {
          updateSidenavSections({
            content: {...content, is_favorited: isFavorited},
          });
        },
        successMessage: isFavorited ? t(`sidenav.favorite-success`) : t(`sidenav.unfavorite-success`),
        errorMessage: isFavorited ? t(`sidenav.favorite-error`) : t(`sidenav.unfavorite-error`),
        successTrackingEvent: isFavorited ? DashboardTrackingEvent.Favorited : DashboardTrackingEvent.Unfavorited,
      });
    } else {
      favoriteContentRequest(content, (result) => updateSidenavSections({content: result}));
    }
  };

  const pinContent = (content: ContentItemInfo) => {
    // Toggle pinned status. `YYYY-MM-DD` format
    const pinnedDate = content.pinned_date ? null : new Date().toISOString().substring(0, 10);

    if (isDashboardItemInfo(content)) {
      const dashboard = contentItemInfoToDashboardItemInfo(content);

      performDashboardAction({
        dashboard,
        action: () => {
          return dashboardsClient.pinDashboard({
            projectId,
            workspaceId: parseInt(workspaceId),
            dashboardID: dashboard.id,
            isPinned: Boolean(pinnedDate),
          });
        },
        onSuccess: () => {
          updateSidenavSections({
            content: {...content, pinned_date: pinnedDate},
          });
          const seenPinnedContentIds = seenPinnedContentIdsStorage.getItem({userId, key: projectId}) || [];
          if (seenPinnedContentIds.indexOf(content.id) === -1) {
            seenPinnedContentIdsStorage.setItem({
              userId,
              key: projectId,
              value: [...seenPinnedContentIds, content.id],
            });
          }
        },
        successMessage: pinnedDate ? t(`sidenav.pin-success`) : t(`sidenav.unpin-success`),
        errorMessage: pinnedDate ? t(`sidenav.pin-error`) : t(`sidenav.unpin-error`),
        successTrackingEvent: pinnedDate ? DashboardTrackingEvent.Pinned : DashboardTrackingEvent.Unpinned,
      });
    } else {
      pinContentRequest(content, (result) => updateSidenavSections({content: result}));
    }
  };

  const renameContent = async ({content}: {content: ContentItemInfo}) => {
    if (isDashboardItemInfo(content)) {
      const dashboard = contentItemInfoToDashboardItemInfo(content);
      const stagedDashboard = new StagedDashboard(dashboard);
      const createEntityModalResult = await promptManageDashboardModal(
        i18n.t(`sidenav.rename-board`),
        stagedDashboard.getCreateEntityModalAttrs({
          projectId: parseInt(projectId),
          i18n,
        }),
      );

      if (!createEntityModalResult.didSave) {
        return null;
      }
      const {name: title, description} = createEntityModalResult;

      const updatedDashboard = await dashboardsClient.updateDashboardFields({
        projectId,
        dashboardID: stagedDashboard.dashboard.id ?? 0,
        fields: {title, ...(description ? {description} : {})},
      });

      updateContentSidenavItem({content: updatedDashboard, sidenavSections});
      try {
        trackEvent(DashboardTrackingEvent.EditDashboardSettings, {
          ...stagedDashboard.toTrackingData(),
          ...myDashboardTrackingProps(updatedDashboard.id),
        });
      } catch (err) {
        reporter.error(err, `Error tracking board rename.`);
      }

      return updatedDashboard;
    }
    content = content as ContentItemInfo;
    const entityType = getEntityType(content.content_type!);
    const createEntityModalResult = await promptManageContentModal(
      i18n.t(`sidenav.rename-content`, {entityTypeName: i18n.print(getEntityTypeName(entityType))}),
      entityType,
      buildCreateEntityModalAttrs({
        entity: normalizeContentEntity(content),
        entityType,
        isDuplicating: false,
        isEditing: true,
        modalOpen: true,
        projectId: parseInt(projectId),
        i18n,
      }),
    );

    if (!createEntityModalResult.didSave) {
      return null;
    }
    const {name, description} = createEntityModalResult;

    return renameContentRequest(content, {name, description}, (result) => {
      updateContentSidenavItem({content: result});
    });
  };

  const duplicateContent = async (content: ContentItemInfo) => {
    if (isDashboardItemInfo(content)) {
      const dashboard = contentItemInfoToDashboardItemInfo(content);
      const fields = {duplicate: dashboard.id};
      let newDashboard = null;

      try {
        trackEvent(DashboardTrackingEvent.ClickDuplicateDashboard, {
          ...getCommonDashboardTrackingProps({
            dashboard,
            // cards: this.getAvailableCards(),
          }),
          'Duplicate Location': `Sidenav`,
        });

        newDashboard = await selectorController.createDashboard({fields});

        navigate(
          `#${serializeDashboardUrlHashParams({
            dashboardId: newDashboard.id,
          })}`,
        );
        trackEvent(DashboardTrackingEvent.DuplicateDashboard, {
          // can currently only duplicate from sidenav
          ...getCommonDashboardTrackingProps({
            dashboard,
            cards: newDashboard.cards,
            sourceTrackingProps: {crudSource: DashboardCRUDSource.Sidenav},
          }),
          ...myDashboardTrackingProps(dashboard.id),
        });
        insertContentSidenavItem(
          dashboardItemInfoToContentItemInfo(newDashboard),
          SidenavSectionIndex.Yours,
          SidenavSectionTrackingName.Yours,
        );
      } catch (err) {
        const message = t(`sidenav.duplicate-error`);
        reporter.error(err as Error, `Error duplicating board.`);

        addToast({
          type: ToastType.ERROR,
          message,
          timeout: 20000,
        });
      }

      selectorController.debouncedStopSavingChanges();
      return dashboard;
    }
    duplicateContentRequest(content, (result) =>
      insertContentSidenavItem(result, SidenavSectionIndex.Yours, SidenavSectionTrackingName.Yours),
    );
    return content;
  };

  const deleteSidenavItem: DeleteSidenavItemFn = (contentId, sectionIndices) => {
    setSidenavSections((prevSidenavSections) => {
      let newSidenavSections = [...prevSidenavSections];

      const indicesToDelete = [];
      // When a dashboard is deleted, its children are moved to the root level
      const topLevelChildrenToMove: SidenavSectionItem[] = [];

      for (const [sectionIndex, section] of newSidenavSections.entries()) {
        if (!sectionIndices || sectionIndices.includes(sectionIndex)) {
          for (const [itemIndex, item] of section.items.entries()) {
            const {updatedItem, removedChildren} = getSidenavItemWithoutValue(contentId, item);

            removedChildren
              .filter((childItem) => childItem.content.creator_id === userId)
              .forEach((childToMove) => topLevelChildrenToMove.push(childToMove));

            if (updatedItem) {
              newSidenavSections = sidenavItemSplice(newSidenavSections, sectionIndex, itemIndex, 1, updatedItem);
            } else {
              indicesToDelete.push({sectionIndex, itemIndex});
            }
          }
        }
      }

      for (const {sectionIndex, itemIndex} of indicesToDelete) {
        if (!sectionIndices || sectionIndices?.includes(sectionIndex)) {
          newSidenavSections = sidenavItemSplice(newSidenavSections, sectionIndex, itemIndex, 1);
        }
      }

      const uniqueChildrenToMove = uniqBy(topLevelChildrenToMove, (childItem) => childItem.content.id);

      for (const childItem of uniqueChildrenToMove) {
        if (!newSidenavSections[2].items.some((item) => isValueInDashboardItem(item, childItem.value))) {
          newSidenavSections = sidenavItemSplice(newSidenavSections, 2, undefined, undefined, childItem);
        }
      }

      return newSidenavSections;
    });
  };

  const deleteContent = async (content: ContentItemInfo, bypassConfirm?: boolean) => {
    const isDashboard = isDashboardItemInfo(content);
    const dashboard = isDashboard ? contentItemInfoToDashboardItemInfo(content) : undefined;
    if (isDashboard) {
      trackDashboardEvent(dashboard!, DashboardTrackingEvent.ClickedDeleteDashboard);
    } else {
      trackContentEvent(content as ContentItemInfo, ContentTrackingEvent.ClickedDeleteContent);
    }

    let confirmationMessage = isDashboard
      ? t(`sidenav.delete-confirm-body`, {boardName: content.name})
      : t(`sidenav.delete-confirm-body-content`, {
          name: (content as ContentItemInfo).name,
          entityTypeName: i18n.print(getEntityTypeName(getEntityType(content.content_type!))),
        });

    if (isDashboard && dashboard!.child_dashboards?.length) {
      confirmationMessage += ` ` + t(`sidenav.children-preserved`);
    }

    confirmationMessage += `\n\n${t(`sidenav.permanent-action`)}`;

    if (isAllowedSuperadminOverride(content)) {
      const message = content.allow_staff_override
        ? t(`sidenav.access-override-staff`)
        : t(`sidenav.access-override-owner`);
      confirmationMessage = `${message}\n\n${confirmationMessage}`;
    }

    if (
      bypassConfirm ||
      (await confirm({
        title: t(`sidenav.delete-confirm-title`),
        description: confirmationMessage,
        theme: ConfirmTheme.DELETE,
        'confirm-button-label': t(`common.delete`),
        'description-allow-newline': true,
      }))
    ) {
      if (isDashboard) {
        performDashboardAction({
          dashboard: dashboard!,
          action: () => {
            return selectorController.deleteDashboard(dashboard!.id);
          },
          onSuccess: () => {
            deleteSidenavItem(dashboard!.id.toString());
          },
          onComplete: () => {
            selectorController.debouncedStopSavingChanges();
          },
          errorMessage: t(`sidenav.delete-error`),
          successTrackingEvent: DashboardTrackingEvent.DeleteDashboard,
        });
      } else {
        deleteContentRequest(content, (contentId) => deleteSidenavItem(contentId));
      }
    }
  };

  const openExternalDashboard = (
    contentId: string,
    focusedContent?: {
      contentId: string | number;
      contentType: ContentType;
    },
  ) => {
    const focusedContentUrlParam = focusedContent
      ? `&card-id=${getCardIdFromContent(focusedContent.contentId, focusedContent.contentType)}`
      : ``;

    openInNewTab(
      getReportUrl(`boards/#id=${contentId}`, {
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      }) + focusedContentUrlParam,
      `_blank`,
    );
  };

  const loadSidenavSections: LoadSidenavSectionsFn = async () => {
    const currProjectId = Number(getProjectId());

    const loadedSidenavSections = await getSidenavSections({
      userId,
      projectId: currProjectId,
      seenPinnedContentIdsStorage,
      hideDashboardList: !isNestedDashboardsEnabled(currentProjectValue!),
      dashboardSelectorController: selectorController,
      isEnterpriseFeatureGateTier: isOrgOnEnterprisePlan(currentProjectValue!),
      isNestedDashboardsEnabled: isNestedDashboardsEnabled(currentProjectValue!),
      isCrossProjectBoardMovesEnabled: isCrossProjectBoardMovesEnabled(currentProjectValue!),
    });

    setSidenavSections(loadedSidenavSections);
    setIsLoading(false);

    trackEvent(
      DashboardTrackingEvent.DashboardSidenavLoaded,
      loadedSidenavSections.reduce(
        (countHash, section) => ({
          ...countHash,
          [`${section.trackingName} dashboards`]: section.items.length,
        }),
        {},
      ),
    );
  };

  const moveSidenavItem: MoveSidenavItemFn = (
    content,
    sidenavItemToMove,
    parentDashboardId,
    currentSidenavItemPosition,
    destinationSidenavItemPosition,
  ) => {
    if (!sidenavItemToMove) {
      // When moving from header menu, there may not be an existing sidenav item to move
      sidenavItemToMove = findFirstSidenavItemWithValue(sidenavSections, content.id);
    }

    if (!sidenavItemToMove) {
      return;
    }

    // When the move is initiated from the header or sidenav menus, there may not be an existing destination sidenav item to move to
    if (!currentSidenavItemPosition || !destinationSidenavItemPosition) {
      currentSidenavItemPosition = {
        sectionIndex: sidenavItemToMove.sectionIdx,
        itemIndex: sidenavItemToMove.itemIdx,
      };

      if (!parentDashboardId) {
        destinationSidenavItemPosition = {
          parentItem: undefined,
          sectionIndex: 2,
          itemIndex: 0,
        };
      } else {
        destinationSidenavItemPosition = {parentItem: {value: parentDashboardId.toString()} as SidenavSectionItem};
      }
    }

    const sections = getSectionsAfterMovingSidenavItem(
      sidenavSections,
      sidenavItemToMove as ContentSidenavSectionItem,
      currentSidenavItemPosition,
      destinationSidenavItemPosition!,
      userId,
    );

    if (!parentDashboardId) {
      if (!destinationSidenavItemPosition?.parentItem) {
        if (destinationSidenavItemPosition?.sectionIndex === SidenavSectionIndex.Favorites && !content.is_favorited) {
          favoriteContent(content);
        } else if (
          destinationSidenavItemPosition?.sectionIndex === SidenavSectionIndex.Pinned &&
          !content.pinned_date
        ) {
          pinContent(content);
        }
      }

      updateSidenavOrdering(sections, Number(projectId));
    }

    updateContentSidenavItem({content, sidenavSections: sections});
  };

  const moveDashboard = (
    dashboard: DashboardItemInfo,
    source: DashboardCRUDSource,
    parentDashboardId?: number,
    currentSidenavItem?: ContentSidenavSectionItem,
    currentSidenavItemPosition?: SidenavItemPosition,
    destinationSidenavItemPosition?: SidenavItemPosition,
  ) => {
    try {
      moveBoardViaDashboardClient(dashboard, workspaceId, projectId, parentDashboardId).then(() => {
        loadSidenavSections();
      });

      addToast({
        cta: t(`sidenav.visit-board`),
        message: t(`sidenav.board-moved`),
        type: ToastType.SUCCESS,
        undoable: false,
        ctaCallback: () => {
          if (parentDashboardId) {
            openExternalDashboard(String(parentDashboardId), {
              contentId: dashboard.id,
              contentType: ContentType.Dashboard,
            });
          } else {
            openExternalDashboard(String(dashboard.id));
          }
        },
      });

      moveSidenavItem(
        dashboardItemInfoToContentItemInfo(dashboard),
        currentSidenavItem,
        parentDashboardId,
        currentSidenavItemPosition,
        destinationSidenavItemPosition,
      );

      trackEvent(DashboardTrackingEvent.MoveDashboard, {
        ...getCommonDashboardTrackingProps({
          dashboard,
        }),
        ...myDashboardTrackingProps(dashboard.id),
        Source: source,
      });
    } catch (err) {
      let message = t(`sidenav.move-error`);

      if (err instanceof ApiResponseError) {
        message = err.message;
      }
      reporter.error(err as Error, `Error moving board.`);
      addToast({
        message,
        type: ToastType.ERROR,
        timeout: 20000,
      });
    }
  };

  const createDashboardInTargetProject = (
    dashboard: DashboardItemInfo,
    targetProjectId?: number,
    targetParentDashboardId?: number,
  ): Promise<Dashboard | null> => {
    return selectorController
      .createDashboard({
        fields: {
          duplicate: dashboard.id,
          target_project_id: targetProjectId,
          target_parent_dashboard_id: targetParentDashboardId,
        },
      })
      .catch((err) => {
        reportErrorOnMovingBoard(err);
        return null;
      });
  };

  const moveToYourBoards = async (
    dashboard: DashboardItemInfo,
    targetProjectId?: number,
    targetParentDashboardId?: number,
  ): Promise<string | undefined> => {
    if (targetProjectId === undefined) {
      // We're being asked to move a board to the place it already is. Done!
      return undefined;
    }

    const movedBoard = await createDashboardInTargetProject(dashboard, targetProjectId, targetParentDashboardId);

    if (movedBoard === null) {
      return undefined;
    }

    try {
      selectorController.deleteDashboard(dashboard.id);
      deleteSidenavItem(dashboard.id.toString());
      return getReportUrl(`boards/#id=${movedBoard.id}`, {
        projectId: targetProjectId,
      });
    } catch (err) {
      // If we fail to delete the board, we should undo the createDashboardInTargetProject(delete the board from the target project).
      reportErrorOnMovingBoard(err);

      const targetDashboardController = new DashboardSelectorController({
        projectId: targetProjectId,
        bookmarkID: undefined,
        dashboardsClient: dashboardsClient as unknown as DashboardsModelClient,
        i18n,
      });
      await targetDashboardController.deleteDashboard(movedBoard.id);
      return undefined;
    }
  };

  const executeMoveToYourBoardsWorkflow = async (
    dashboardToMove: DashboardItemInfo,
    targetProjectId?: number,
    targetParentDashboardId?: number,
  ): Promise<void> => {
    if (targetProjectId === undefined || targetProjectId === Number(projectId)) {
      // We're being asked to move a board to the project it is already in. Done!
      return;
    }

    addToast({
      message: t(`sidenav.moving`),
      type: ToastType.INFO,
    });

    movedBoardInfoStorage.current.setItem({
      userId,
      key: MOVED_BOARD_STORAGE_KEY,
      value: {
        sourceProjectId: projectId,
        sourceDashboardId: dashboardToMove.id,
        undoable: true,
      },
    });

    const newBoardUrl = await moveToYourBoards(dashboardToMove, targetProjectId, targetParentDashboardId);

    if (newBoardUrl !== undefined) {
      // TODO: use "navigate" when it's ready
      window.location.assign(newBoardUrl);
    }
  };

  const handleSidenavOverflowMenuAction = (ev: CustomEvent) => {
    const {item, menuItem}: {item: ContentSidenavSectionItem; menuItem: any} = ev.detail;
    if (!item.content) {
      return;
    }
    const isDashboard = isDashboardItemInfo(item.content);
    const dashboard = isDashboard ? contentItemInfoToDashboardItemInfo(item.content) : undefined;
    const content = item.content;
    const actionName = menuItem.name || menuItem.value;
    switch (actionName) {
      case SidenavOverflowAction.Link:
        copySidenavLink(item.href, i18n);
        if (isDashboard) {
          trackDashboardEvent(dashboard!, DashboardTrackingEvent.CopySidenavLink);
        } else {
          trackContentEvent(content, ContentTrackingEvent.CopySidenavLink);
        }
        break;
      case SidenavOverflowAction.Favorite:
        favoriteContent(content);
        break;
      case SidenavOverflowAction.Pin:
        pinContent(content);
        break;
      case SidenavOverflowAction.Edit:
        renameContent({content});
        break;
      case SidenavOverflowAction.Duplicate:
        duplicateContent(content);
        break;
      case SidenavOverflowAction.Delete:
        deleteContent(content);
        break;
      case SidenavOverflowAction.Share:
        sharingWidget.promptSharing({
          type: content.content_type!,
          id: content.id,
          'project-id': parseInt(projectId),
        });
        break;
      case SidenavOverflowAction.Move:
        if (isDashboard) {
          if (menuItem.additionalInfo.targetProjectId === parseInt(projectId)) {
            moveDashboard(dashboard!, DashboardCRUDSource.Sidenav, menuItem.value, item);
          } else {
            executeMoveToYourBoardsWorkflow(dashboard!, menuItem.additionalInfo.targetProjectId, menuItem.value);
          }
        }
        break;
      default:
        break;
    }

    if (isDashboard) {
      trackEvent(DashboardTrackingEvent.DashboardSidenavOverflowMenuActionSelected, {'Selected Action': actionName});
    } else {
      trackEvent(ContentTrackingEvent.ContentSidenavOverflowMenuActionSelected, {
        'Selected Action': actionName,
        'content type': item.content?.content_type,
      });
    }
  };

  const createDashboard = async (stagedDashboard: StagedDashboard, globalAccessType?: GlobalAccessType) => {
    const fields = {
      ...maybeAddGlobalAccessTypeParam(stagedDashboard.serialize(), globalAccessType, globalAccessType),
    };

    const dashFields = omit(fields, [`cards`, `card_order`]);
    const dashboard = await selectorController.createDashboard({title: fields.title, fields: dashFields});
    try {
      if (stagedDashboard.isDuplicating()) {
        trackEvent(DashboardTrackingEvent.DuplicateDashboard, {
          ...stagedDashboard.toTrackingData(),
          ...myDashboardTrackingProps(stagedDashboard.duplicate),
        });
      } else {
        trackEvent(DashboardTrackingEvent.CreateDashboard, {
          ...stagedDashboard.toTrackingData(),
        });
      }
    } catch (err) {
      reporter.error(err, `Error tracking board event.`);
    }

    insertContentSidenavItem(
      dashboardItemInfoToContentItemInfo(dashboard),
      SidenavSectionIndex.Yours,
      SidenavSectionTrackingName.Yours,
    );

    navigate(
      getReportUrl(`/boards#id=${dashboard.id}`, {
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      }),
    );
    return dashboard;
  };

  const createNewContent = async () => {
    return createDashboard(new StagedDashboard({title: `Untitled`}, {crudSource: DashboardCRUDSource.Sidenav}));
  };

  const handleSidenavHeaderActionClicked = async (ev: CustomEvent): Promise<void> => {
    if (sidenavState.confirmHeaderAction && !(await sidenavState.confirmHeaderAction())) {
      return;
    }
    if (ev.detail.value === SidenavHeaderAction.New) {
      createNewContent();
    }
  };

  const handleSidenavDragDrop = (ev: CustomEvent<DragDropEventDetail>) => {
    const item = ev.detail.item as ContentSidenavSectionItem;
    if (!item.content) return;

    // Only allow dashboard-to-dashboard nesting, not content
    const isDashboard = isDashboardItemInfo(item.content);
    const parentItemContent = ev.detail.destinationPosition.parentItem?.content;
    const isParentDashboard = parentItemContent && isDashboardItemInfo(parentItemContent);

    if (!getSidenavItemParentValue(item) && !ev.detail.destinationPosition.parentItem) {
      const sections = moveSidenavItemAtTopLevel(
        sidenavSections,
        ev.detail.sectionIndex,
        ev.detail.itemIndex,
        ev.detail.destinationPosition.sectionIndex!,
        ev.detail.destinationPosition.itemIndex!,
      );
      updateSidenavOrdering(sections, parseInt(getProjectId()));
      setSidenavSections(sections);
    } else if (isDashboard && isParentDashboard) {
      // Only allow dashboard-to-dashboard nesting
      moveDashboard(
        contentItemInfoToDashboardItemInfo(item.content),
        DashboardCRUDSource.Sidenav,
        ev.detail.destinationPosition.parentItem ? parseInt(ev.detail.destinationPosition.parentItem.value) : undefined,
        item,
        {sectionIndex: ev.detail.sectionIndex, itemIndex: ev.detail.itemIndex},
        ev.detail.destinationPosition,
      );
    } else {
      addToast({
        type: ToastType.ERROR,
        message: t(`sidenav.no-nested-content`),
        timeout: 20000,
      });
    }
  };

  const getSidenavProps: GetSidenavPropsFn = () =>
    ({
      /** attrs */
      elref: `sidenav`,
      selected: sidenavState.selectedNavItem,
      loading: isLoading,
      header: getSidenavHeader(),
      userId,
      storageKey: `dashboards`,
      fixedSize: false,
      isNestedDashboardsEnabled: isNestedDashboardsEnabled(currentProjectValue!),

      /** props */
      sections: sidenavSections,
      preventDefaultNavigate: sidenavState.preventDefaultNavigate,

      /** handlers */
      onSelectItem: handleSidenavSelectItem,
      onHeaderAction: handleSidenavHeaderActionClicked,
      onDragDrop: handleSidenavDragDrop,
      onOverflowMenuSelect: handleSidenavOverflowMenuAction,
    }) satisfies SidenavProps;

  return (
    <ContentSidenavContext.Provider
      value={{
        getSidenavProps,
        moveSidenavItem,
        deleteSidenavItem,
        insertContentSidenavItem,
        updateContentSidenavItem,
        updateSidenavSections,
        loadSidenavSections,
        createNewContent,
        sidenavState,
        setSidenavState,
        updateSidenavState,
      }}
    >
      {props.children}
    </ContentSidenavContext.Provider>
  );
};

export const useContentSidenavContext = () => React.useContext(ContentSidenavContext);
